/**
 * Behavioral probe for WebM alpha-channel support. Safari reports WebM/VP9 as
 * decodable via canPlayType, but renders the alpha channel as opaque/black —
 * canPlayType is therefore unreliable. This probe loads a tiny inline VP9
 * WebM with a fully transparent frame, draws it to a 1x1 canvas, and inspects
 * the resulting alpha pixel.
 *
 * Sibling implementation lives in embed-service at
 * `src/js/lib/supports-webm-alpha.js`.
 */

import { isSafari } from './device';

const WEBM_ALPHA_TEST_SRC =
  'data:video/webm;base64,GkXfowEAAAAAAAAfQoaBAUL3gQFC8oEEQvOBCEKChHdlYm1Ch4ECQoWBAhhTgGcBAAAAAAACBRFNm3RALE27i1OrhBVJqWZTrIHlTbuMU6uEFlSua1OsggEjTbuMU6uEHFO7a1OsggHo7AEAAAAAAACqAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmAQAAAAAAADIq17GDD0JATYCNTGF2ZjU3LjU3LjEwMFdBjUxhdmY1Ny41Ny4xMDBEiYhARAAAAAAAABZUrmsBAAAAAAAARq4BAAAAAAAAPdeBAXPFgQGcgQAitZyDdW5khoVWX1ZQOYOBASPjg4QCYloA4AEAAAAAAAARsIFAuoFAmoECU8CBAVSygQQfQ7Z1AQAAAAAAAGfngQCgAQAAAAAAAFuhooEAAACCSYNCAAPwA/YAOCQcGFQAADBgAABnP///NXgndmB1oQEAAAAAAAAtpgEAAAAAAAAk7oEBpZ+CSYNCAAPwA/YAOCQcGFQAADBgAABnP///Vttk7swAHFO7awEAAAAAAAARu4+zgQC3iveBAfGCAXXwgQM=';

const PROBE_TIMEOUT_MS = 2000;

let cachedResult: boolean | undefined;
let inFlight: Promise<boolean> | undefined;

function readAlphaFromVideo(video: HTMLVideoElement): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return false;
  }

  try {
    ctx.drawImage(video, 0, 0, 1, 1);
    const alpha = ctx.getImageData(0, 0, 1, 1).data[3];
    return alpha === 0;
  } catch {
    // drawImage can throw if the video element is in a bad state, or
    // getImageData can throw due to a tainted canvas (shouldn't happen with a
    // same-origin data URL but guard anyway).
    return false;
  }
}

function runProbe(): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.style.display = 'none';

    let settled = false;
    const finish = (result: boolean) => {
      if (settled) {
        return;
      }

      settled = true;

      window.clearTimeout(timeoutId);

      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }

      resolve(result);
    };

    const onLoaded = () => finish(readAlphaFromVideo(video));
    const onFailure = () => finish(false);

    const timeoutId = window.setTimeout(() => finish(false), PROBE_TIMEOUT_MS);

    video.addEventListener('loadeddata', onLoaded, { once: true });
    video.addEventListener('error', onFailure, { once: true });
    video.addEventListener('abort', onFailure, { once: true });
    video.addEventListener('stalled', onFailure, { once: true });

    document.body.appendChild(video);
    video.src = WEBM_ALPHA_TEST_SRC;
  });
}

/**
 * Kick off (or return the in-flight) WebM alpha capability probe. The result
 * is cached at module level — subsequent calls return the same promise.
 * Always resolves; never rejects.
 */
export async function initSupportsWebmAlpha(): Promise<boolean> {
  if (cachedResult !== undefined) {
    return cachedResult;
  }

  if (inFlight) {
    return inFlight;
  }

  inFlight = runProbe();
  cachedResult = await inFlight;
  inFlight = undefined;

  return cachedResult;
}

/**
 * Synchronous read of the cached probe result. If the probe hasn't resolved
 * yet, returns `!isSafari` as a smart UA-based default: Chrome / Firefox /
 * Edge all decode VP9 alpha correctly, so the optimistic default is correct
 * for the vast majority of first-render moments. The probe corrects the
 * answer once it settles (typically within ~100ms).
 */
export function getSupportsWebmAlphaSync(): boolean {
  return cachedResult ?? !isSafari;
}
